/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import getConfig from "next/config";
import React from "react";
import { twMerge } from "tailwind-merge";

import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useIsEcoBonusEligible } from "@hooks/useIsEcoBonusEligible";
import { useIsMounted } from "@hooks/useIsMounted";

import { priceAlgorithm } from "@lib/priceAlgorithm";

import {
  useIsKeyAccount,
  useIsUserTrustedSelector,
} from "@redux/reducers/auth";
import {
  useCEESubsidySelector,
  useTaxPercentageSelector,
} from "@redux/reducers/appConfig";
import {
  useActiveCarPreconfigurationSelector,
  useCarConfigurationSelector,
  useCurrentDealSelector,
  useDealDeliveryMethodSelector,
  useDealerSelector,
  useDealSelector,
  useIsB2BLeasysSelected,
  useIsCEESubsidySelectedSelector,
  useIsScrappageSelectedSelector,
  useSelectedJourneySelector,
} from "@redux/reducers/deal";

import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";
import { getVehicleRegistrationType } from "@shared/v2/lib/getVehicleRegistrationType";

const { publicRuntimeConfig } = getConfig();

const { COUNTRY } = publicRuntimeConfig;

const Tag = ({ className, label }) => (
  <span
    className={twMerge(
      "bg-primaryDarkAccent/20 text-primaryDark text-xs px-1.5 py-0.5 rounded-md",
      className
    )}
  >
    {label}
  </span>
);

export const SessionState = () => {
  const activePreconfiguration = useActiveCarPreconfigurationSelector();
  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);
  const currentDeal = useCurrentDealSelector();
  const deal = useDealSelector();
  const deliveryMethod = useDealDeliveryMethodSelector();
  const deliveryDealer = useDealerSelector();
  const isB2BLeasysSelected = useIsB2BLeasysSelected();
  const isB2BSelected = useIsB2BSelected();
  const isKeyAccount = useIsKeyAccount();
  const isMounted = useIsMounted();
  const isUserTrusted = useIsUserTrustedSelector();
  const selectedJourney = useSelectedJourneySelector();
  const taxPercentage = useTaxPercentageSelector();
  const withEcobonus = useIsEcoBonusEligible();
  const withScrappage = useIsScrappageSelectedSelector();
  const { accessory, service } = useCarConfigurationSelector();

  const carteGrisePrice = priceAlgorithm.getPrice(
    getCarteGriseAmount(deal, true),
    taxPercentage
  );
  const journey = isB2BLeasysSelected ? "B2B Leasys" : selectedJourney;
  const withCEESubsidy = ceeSubsidy?.enabled && !!ceeSubsidySelected;
  const withCarteGrise =
    !!carteGrisePrice &&
    carteGrisePrice.netPrice > 0 &&
    getIsVehicleRegistrationByCitroenSelected(
      deal.financeSimulation?.vehicleRegistration
    );
  const {
    userProfile,
    financeSimulation: { promoCode },
  } = deal || {};
  const { externalId } = currentDeal || {};

  if (!isMounted) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 text-xs text-primaryDark max-w-110">
      <div className="flex flex-col gap-0.5 items-start border-b border-primaryDarkAccent/20 pb-1 text-xxs font-mono">
        {window.location.href}
        <br />
        <Tag
          label={`${dayjs().format("DD/MM/YYYY HH:mm:ss")}`}
          className="text-xxs"
        />
      </div>
      <div className="flex flex-col gap-0.5 items-start border-b border-primaryDarkAccent/20 pb-1 whitespace-nowrap">
        <div>
          <b className="font-semibold">User: </b>
          {isUserTrusted ? (
            <span className="text-green-600">{userProfile?.customerId}</span>
          ) : (
            <span className="text-primaryOrange"> Anonymous</span>
          )}
        </div>
        {isUserTrusted && userProfile && (
          <div>
            {userProfile?.userFirstName} {userProfile?.userLastName} &lt;
            {userProfile?.email}&gt; <br />
          </div>
        )}
        {isKeyAccount && <Tag label="Key Account" />}
      </div>
      <div className="flex flex-col gap-0.5 items-start border-b border-primaryDarkAccent/20 pb-1 whitespace-nowrap">
        <div>
          <b className="font-semibold">Vehicle: </b>
          {activePreconfiguration.label}
        </div>
        <div className="flex flex-wrap gap-1">
          <Tag label={activePreconfiguration.lcdvCode} />
          <Tag label={activePreconfiguration.id} />
        </div>
        {accessory.length > 0 &&
          accessory.map(item => (
            <Tag key={item.id} label={`A: ${item.label} (${item.id})`} />
          ))}
        {service.length > 0 &&
          service.map(item => (
            <Tag key={item.id} label={`S: ${item.label} (${item.id})`} />
          ))}
      </div>
      <div className="flex flex-col gap-0.5 items-start border-b border-primaryDarkAccent/20 pb-1 whitespace-nowrap">
        <div>
          <b className="font-semibold">Journey: </b>
        </div>
        <div className="flex flex-wrap gap-1">
          <Tag label={COUNTRY} />
          <Tag label={isB2BSelected ? "B2B" : "B2C"} />
          <Tag label={journey} />
          {!!deliveryMethod?.id && (
            <Tag
              label={`Delivery: ${deliveryMethod.label} (${deliveryMethod.id})`}
            />
          )}
          {!!deliveryDealer?.name && (
            <Tag
              label={`Dealer: ${deliveryDealer.name} (${deliveryDealer.RRDICode})`}
            />
          )}
          {!!deal.financeSimulation?.vehicleRegistration && (
            <Tag
              label={`Registration: ${getVehicleRegistrationType(
                deal.financeSimulation.vehicleRegistration
              )}`}
            />
          )}
          {externalId && <Tag label={externalId} />}
          {withCarteGrise && <Tag label="CarteGrise" />}
          {withCEESubsidy && <Tag label={`CEE (${ceeSubsidySelected})`} />}
          {withEcobonus && <Tag label="Ecobonus" />}
          {withScrappage && <Tag label="Scrappage" />}
        </div>
      </div>
      {promoCode && (
        <div className="flex flex-col gap-0.5 items-start border-b border-primaryDarkAccent/20 pb-1 whitespace-nowrap">
          <div>
            <b className="font-semibold">Promo Code: </b>
          </div>
          <div className="flex flex-wrap gap-1">
            <Tag label={`ID: ${promoCode.id}`} />
            <Tag label={`Code: ${promoCode.code}`} />
            <Tag label={`Label: ${promoCode.label}`} />
            <Tag label={`Valid: ${promoCode.valid.toString()}`} />
            <Tag label={`Deposit Amount: ${promoCode.depositAmount}`} />
            <Tag label={`DiscountType: ${promoCode.discountType}`} />
            <Tag label={`Discount Amount: ${promoCode.discountAmount}`} />
            <Tag
              label={`Discount Percentage: ${promoCode.discountPercentage}`}
            />
            <Tag
              label={`End Date: ${dayjs(promoCode.endDate).format(
                "DD/MM/YYYY"
              )}`}
            />
            <Tag label={`Journey Type: ${promoCode.journeyType}`} />
          </div>
        </div>
      )}
    </div>
  );
};
